import { BrowserModule, DomSanitizer } from "@angular/platform-browser";
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { HttpClientModule } from "@angular/common/http";
import { RouterModule } from "@angular/router";
import { NgxMaskModule } from "ngx-mask";
import { IonicModule } from "@ionic/angular";

import { GooglePlaceModule } from "ngx-google-places-autocomplete";
import { AppComponent } from "./app.component";
import { CardComponent } from "./featured-card/card.component";
import { NavMenuComponent } from "./nav-menu/nav-menu.component";
import { LoginComponent } from "./nav-menu/login.component";
import { RegisterComponent } from "./register/register.component";

// Home Page
import { HomeComponent } from "./home/home.component";
import { InsuranceService } from "./home/insurance.service";
import { SearchService } from "./home/search.service";
import { WatchVideoDialog } from "./home/modals/watch-video.component";
// Landing Pages
import { PhysicianLandingComponent } from "./physicianLanding/home.component";
import { MedicalServicesLandingComponent } from "./medicalServicesLanding/home.component";
import { MedicalRetailersLandingComponent } from "./medicalRetailersLanding/home.component";

import { SignupOrganizationComponent } from "./signup-organization/signup-organization.component";
// import { SignupOrganizationComponent } from "./signup-organization/signup-organization.component";

// Coming Soon Page
import { ComingSoonComponent } from "./comingSoon/comingSoon.component";
import { PhysicianModule } from "./physician/physician.module";
import { UserService } from "./core/services/v2/user.service";
import { PatientService } from "./patients/patients.service";
import { AgmCoreModule } from "@agm/core";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatTableModule } from "@angular/material/table";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTabsModule } from "@angular/material/tabs";
import { MatMenuModule } from "@angular/material/menu";
import { MatCheckboxModule } from "@angular/material/checkbox";

import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import {
  MatButtonModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatCardModule,
  MatStepperModule,
  MatDividerModule,
  MatSnackBarModule,
  MatTooltipModule,
  MatToolbarModule,
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from "@angular/material";
import { MatSliderModule } from "@angular/material/slider";
import { MatDialogModule } from "@angular/material/dialog";
import { SharedModule } from "./shared/shared.module";
import { CoreModule } from "./core/core.module";
import { SwiperModule } from "ngx-swiper-wrapper";
import { SWIPER_CONFIG } from "ngx-swiper-wrapper";
import { SwiperConfigInterface } from "ngx-swiper-wrapper";

const DEFAULT_SWIPER_CONFIG: SwiperConfigInterface = {
  direction: "horizontal",
  slidesPerView: "auto",
  spaceBetween: 20,
  pagination: false,
};

import { AppointmentDialogComponent } from "./dialog/appointment-dialog/appointment-dialog.component";
import { GetstartComponent } from "./physician/getstart/getstart.component";
import { HealthsystemComponent } from "./physician/healthsystem/healthsystem.component";
import { HealthCategoriesComponent } from "./health-categories/health-categories.component";
import { HealthsystemdialogComponent } from "./health-categories/healthsystemdialog/healthsystemdialog.component";
import { HealthsytemformComponent } from "./health-categories/healthsytemform/healthsytemform.component";
import { HealthComingSoonComponent } from "./health-categories/health-coming-soon/health-coming-soon.component";
import { HealthsysmsgComponent } from "./health-categories/healthsysmsg/healthsysmsg.component";
import { MessageComponent } from "./signup/components/message/message.component";
import { PasswordsetupComponent } from "./signup/components/passwordsetup/passwordsetup.component";
import { TermsandConditionsComponent } from "./termsand-conditions/termsand-conditions.component";
import { MatBadgeModule } from "@angular/material/badge";
import { BAAComponent } from "./baa/baa.component";
import { ContactusComponent } from "./contactus/contactus.component";
import { HealthsyshomeComponent } from "./healthsyshome/healthsyshome.component";
import { ClinicandservicesComponent } from "./clinicandservices/clinicandservices.component";
import { PhysicianslideComponent } from "./physicianslide/physicianslide.component";
import { PatientslideComponent } from "./patientslide/patientslide.component";
import { PayersslideComponent } from "./payersslide/payersslide.component";
import { HeadernavComponent } from "./headernav/headernav.component";
import { AboutusComponent } from "./aboutus/aboutus.component";
import { WhyappyhealthComponent } from "./whyappyhealth/whyappyhealth.component";
import { ApppartnersComponent } from "./apppartners/apppartners.component";
import { AppypartnerscontactinfoComponent } from "./appypartnerscontactinfo/appypartnerscontactinfo.component";
import { PatientModule } from "./patient/patient.module";
import { ReferralsModule } from "./referrals/referrals.module";
import { SimplificationComponent } from "./platform/simplification/simplification.component";
import { InteroperabilityComponent } from "./platform/interoperability/interoperability.component";
import { ContinuumofcareComponent } from "./platform/continuumofcare/continuumofcare.component";
import { TrustedsourceComponent } from "./platform/trustedsource/trustedsource.component";
import { SystemAgnosticComponent } from "./platform/system-agnostic/system-agnostic.component";
import { PersonalComponent } from "./home/personal/personal.component";
import { BaseService } from "./core/services/shared/base.service";
import { EnvironmentService } from "../environments/service/environment.service";
import { UserHomeComponent } from "./user-home/user-home.component";
import { AdminModule } from "./admin/admin.module";
import { CaseManagerModule } from "./case-manager/case-manager.module";
import { AutocompleteLibModule } from "angular-ng-autocomplete";
import { SasagreementComponent } from "./sasagreement/sasagreement.component";
import { MaterialModule } from "./material/material-module";
import { NgSelectModule } from "@ng-select/ng-select";
import { NgScrollbarModule } from "ngx-scrollbar";
import { ChartsModule } from "ng2-charts";
import { CdkTableModule } from "@angular/cdk/table";
import { PDFDialogComponent } from "./signup-organization/pdf-dialog/pdf-dialog.component";
import { PatientNewMinumumComponent } from "./patients/patient-new/patient-new-minumum/patient-new-minumum.component";
import { PatientRegistrationComponent } from "./patient-registration/patient-registration.component";
import { CallCenterModule } from "./call-center/call-center.module";
import { EmployerRegistrationComponent } from "./employer/employer-registration/employer-registration.component";
import { UserLoginComponent } from "./user-login/user-login.component";
import { EmployerModule } from "src/app/employer/employer.module";
import { AgencyModule } from "./agency/agency.module";
import { AgencyRegistrationComponent } from "./agency/agency-registration/agency-registration.component";
import { GeoLocationService } from "./core/services/v2/geolocation.service";
import { SignupOrganizationNewComponent } from "./signup-organization-new/signup-organization-new.component";
import { WelcomeLoginComponent } from './headernav/welcome-login/welcome-login.component';

@NgModule({
  declarations: [
    AppComponent,
    NavMenuComponent,
    LoginComponent,
    RegisterComponent,
    HomeComponent,
    PersonalComponent,
    PhysicianLandingComponent,
    MedicalServicesLandingComponent,
    MedicalRetailersLandingComponent,
    ComingSoonComponent,
    CardComponent,
    WatchVideoDialog,
    HealthCategoriesComponent,
    GetstartComponent,
    HealthsystemComponent,
    AppointmentDialogComponent,
    HealthsystemdialogComponent,
    HealthsytemformComponent,
    HealthComingSoonComponent,
    HealthsysmsgComponent,
    MessageComponent,
    PasswordsetupComponent,
    NavMenuComponent,
    TermsandConditionsComponent,
    BAAComponent,
    ContactusComponent,
    HealthsyshomeComponent,
    ClinicandservicesComponent,
    PhysicianslideComponent,
    PatientslideComponent,
    PayersslideComponent,
    HeadernavComponent,
    AboutusComponent,
    WhyappyhealthComponent,
    ApppartnersComponent,
    AppypartnerscontactinfoComponent,
    SimplificationComponent,
    InteroperabilityComponent,
    ContinuumofcareComponent,
    TrustedsourceComponent,
    SystemAgnosticComponent,
    UserHomeComponent,
    SignupOrganizationComponent,
    SignupOrganizationNewComponent,
    SasagreementComponent,
    PDFDialogComponent,
    EmployerRegistrationComponent,
    UserLoginComponent,
    WelcomeLoginComponent
  ],
  imports: [
    ChartsModule,
    NgSelectModule,
    NgScrollbarModule,
    MatSelectModule,
    GooglePlaceModule,
    BrowserModule.withServerTransition({ appId: "ng-cli-universal" }),
    HttpClientModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    PatientModule,
    ReferralsModule,
    PhysicianModule,
    AdminModule,
    CaseManagerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    BrowserAnimationsModule,
    MatAutocompleteModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatBadgeModule,
    MatIconModule,
    MatSnackBarModule,
    MatButtonModule,
    MatCardModule,
    MatSliderModule,
    CdkTableModule,
    MatDialogModule,
    MatStepperModule,
    MatMenuModule,
    MatToolbarModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    IonicModule.forRoot(),
    SwiperModule,
    SharedModule,
    CoreModule,
    MatDividerModule,
    AutocompleteLibModule,
    MaterialModule,
    CallCenterModule,

    EmployerModule,
    AgencyModule,
    NgxMaskModule.forRoot(),
    AgmCoreModule.forRoot({
      apiKey: "AIzaSyBW8gI-u3gK4VDH1zoYF8z04A81b9Vlnto",
      libraries: ["places"],
    }),
    RouterModule.forRoot(
      [
        {
          path: "",
          redirectTo: "/home",
          pathMatch: "full"
        },
        {
          path: "home",
          component: HomeComponent,

        },
        { path: 'welcome-login', component: WelcomeLoginComponent },
        {
          path: "PatientRegistration",
          component: PatientRegistrationComponent,
        },
        { path: "userhome", component: UserHomeComponent },
        { path: "personal", component: PersonalComponent },
        { path: "contactus", component: ContactusComponent },
        { path: "register", component: RegisterComponent },
        { path: "admin", redirectTo: "/admin", pathMatch: "full" },
        { path: "casemanager", redirectTo: "/casemanager", pathMatch: "full" },
        { path: "physician", redirectTo: "/physician", pathMatch: "full" },
        { path: "patient", redirectTo: "/patient", pathMatch: "full" },
        { path: "referrals", redirectTo: "/referrals", pathMatch: "full" },
        { path: "physicians-claim", component: PhysicianLandingComponent },
        {
          path: "medical-services-claim",
          component: MedicalServicesLandingComponent,
        },
        {
          path: "medical-retailers-claim",
          component: MedicalRetailersLandingComponent,
        },
        { path: "coming-soon", component: ComingSoonComponent },
        { path: "health-categories", component: HealthCategoriesComponent },
        { path: "health-system-form", component: HealthsytemformComponent },
        { path: "health-coming", component: HealthComingSoonComponent },
        { path: "health-message", component: HealthsysmsgComponent },
        { path: "EmailLink", component: MessageComponent },
        { path: "setup-pass", component: PasswordsetupComponent },
        // { path: "signup", component: SignupOrganizationComponent },
        { path: "signup", component: SignupOrganizationNewComponent},
        { path: "healthsys", component: HealthsyshomeComponent },
        { path: "Clinicandservice", component: ClinicandservicesComponent },
        { path: "physicianslide", component: PhysicianslideComponent },
        { path: "patientslide", component: PatientslideComponent },
        { path: "payerslide", component: PayersslideComponent },
        { path: "aboutus", component: AboutusComponent },
        { path: "whyappyhealth", component: WhyappyhealthComponent },
        { path: "appypartners", component: ApppartnersComponent },
        { path: "login", component: UserLoginComponent },
        {
          path: "appypartnercontactinfo",
          component: AppypartnerscontactinfoComponent,
        },

        {
          path: "platform/interoperability",
          component: InteroperabilityComponent,
        },
        {
          path: "platform/continuumofcare",
          component: ContinuumofcareComponent,
        },
        { path: "platform/simplification", component: SimplificationComponent },
        { path: "platform/trustedsource", component: TrustedsourceComponent },
        {
          path: "platform/system-agnostic",
          component: SystemAgnosticComponent,
        },
        {
          path: "register-employer",
          component: EmployerRegistrationComponent,
        },
        { path: "employer", redirectTo: "/employer", pathMatch: "full" },
        {
          path: "register-agency",
          component: AgencyRegistrationComponent,
        },
        { path: "agency", redirectTo: "/agency", pathMatch: "full" },
        { path: "callcenter", redirectTo: "/callcenter", pathMatch: "full" },
      ],
      {
        scrollPositionRestoration: "top",
      }
    ),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    EnvironmentService,
    BaseService,
    UserService,
    PatientService,
    InsuranceService,
    SearchService,
    GeoLocationService,
    {
      provide: SWIPER_CONFIG,
      useValue: DEFAULT_SWIPER_CONFIG,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: "standard" },
    },
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    WatchVideoDialog,
    AppointmentDialogComponent,
    HealthsystemdialogComponent,
    SasagreementComponent,
    TermsandConditionsComponent,
    BAAComponent,
    PDFDialogComponent,
  ],
})
export class AppModule {
  constructor(private matIconRegistry: MatIconRegistry, private domSanitizer: DomSanitizer) {
    this.matIconRegistry.addSvgIcon('new-logo', this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/logo.svg'));
    this.matIconRegistry.addSvgIcon('edit', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/editIcon.svg'));
    this.matIconRegistry.addSvgIcon('new-logo', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/logo.svg'));
    this.matIconRegistry.addSvgIcon('p-default', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-default-control-icon.svg'));
    this.matIconRegistry.addSvgIcon('p-globe', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-globe.svg'));
    this.matIconRegistry.addSvgIcon('p-smartphone', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-smartphone.svg'));
    this.matIconRegistry.addSvgIcon('p-map', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-map.svg'));
    this.matIconRegistry.addSvgIcon('p-map-pin', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-map-pin.svg'));
    this.matIconRegistry.addSvgIcon('p-crosshair', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-crosshair.svg'));
    this.matIconRegistry.addSvgIcon('p-user', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-user.svg'));
    this.matIconRegistry.addSvgIcon('p-mail', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/p-mail.svg'));
    this.matIconRegistry.addSvgIcon('check-circle', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/check-circle.svg'));
    this.matIconRegistry.addSvgIcon('right-arrow', this.domSanitizer.bypassSecurityTrustResourceUrl('/app/assets/icons/right-arrow.svg'));
  }
}
